import { Autocomplete, type Item } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import { useClassStudentsSearchDropdown } from '../../../hooks/useClassStudentsSearchDropdown';

export interface ClassAutocompleteProps {
  /** ID da turma selecionada; `''` quando nenhuma turma está selecionada. */
  readonly value: string;
  /** Notifica o novo ID de turma selecionado (`''` ao limpar). */
  readonly onChange: (classId: string) => void;
  readonly label: string;
  readonly placeholder: string;
}

/**
 * Autocomplete de turmas com busca server-side por nome (debounce + mínimo de 3
 * caracteres), espelhando o autocomplete de cursos da listagem de matrículas.
 *
 * Mantém internamente o termo pesquisado e o rótulo da turma selecionada; para fora
 * expõe apenas o `value` (ID) e o callback `onChange`, mantendo a tela como dona do
 * estado de seleção.
 */
export function ClassAutocomplete({ value, onChange, label, placeholder }: ClassAutocompleteProps) {
  const { t: tCommon } = useTranslation('common');

  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  const { classes, isQuerySuccess } = useClassStudentsSearchDropdown(search);

  const items = classes.map((c): Item<string> => ({ label: c.name, value: c.id }));
  const selectedItem: Item<string> = value
    ? { label: selectedLabel, value }
    : { label: '', value: '' };

  const notFoundMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    items.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <Autocomplete<string>
      label={label}
      placeholder={placeholder}
      size="md"
      list={items}
      custom={{ input: '!w-full' }}
      selectedItem={selectedItem}
      {...(notFoundMessage !== undefined ? { notFoundMessage } : {})}
      onChange={e => setSearch(e.target.value)}
      handleChange={item => {
        onChange(item.value ?? '');
        setSelectedLabel(item.label ?? '');
        setSearch('');
      }}
      handleClearQuery={() => {
        onChange('');
        setSelectedLabel('');
        setSearch('');
      }}
    />
  );
}
